import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useExportSeasonStages } from "~pages/SeasonStages/SeasonStageListPage/hooks/useExportSeasonStages.ts";
import { useImportSeasonStages } from "~pages/SeasonStages/SeasonStageListPage/hooks/useImportSeasonStages.ts";
import type { CreateSeasonStageModel, UpdateSeasonStageModel } from "~pages/SeasonStages/types/seasonStageModel.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { ensureDefaultOperationResponse, isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreateSeasonStage } from "./useCreateSeasonStage.ts";
import { useDeleteSeasonStage } from "./useDeleteSeasonStage.ts";
import { useUpdateSeasonStage } from "./useUpdateSeasonStage.ts";

export const useSeasonStageService = () => {
  const { mutateAsync: createSeasonStageFn, isPending: isCreatePending } = useCreateSeasonStage();
  const { mutateAsync: updateSeasonStageFn, isPending: isUpdatePending } = useUpdateSeasonStage();
  const { mutateAsync: deleteSeasonStageFn, isPending: isDeletePending } = useDeleteSeasonStage();
  const { mutateAsync: importSeasonStagesFn, isPending: isImportPending } = useImportSeasonStages();
  const { mutateAsync: exportSeasonStagesFn, isPending: isExportPending } = useExportSeasonStages();

  const { create, update, remove, execute } = useApiOperations(ApiOperationItems.SEASON_STAGE);

  const createSeasonStage = (payload: CreateSeasonStageModel) =>
    create(() => createSeasonStageFn(payload), {
      successMessageId: "stage.add.success",
    });

  const updateSeasonStage = (stageCode: string, payload: Omit<UpdateSeasonStageModel, "stageCode">) =>
    update(() => updateSeasonStageFn({ ...payload, stageCode }), {
      successMessageId: "stage.update.success",
    });

  const deleteSeasonStage = async (stageCode: string) =>
    Boolean(
      await remove(() => deleteSeasonStageFn(stageCode), {
        successMessageId: "stage.delete.success",
      }),
    );

  const importSeasonStages = async (fileName: string) =>
    ensureDefaultOperationResponse(
      await execute(() => importSeasonStagesFn(fileName), {
        successMessageId: "seasonStages.import.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Season stages import was not started",
    );

  const exportSeasonStages = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    ensureDefaultOperationResponse(
      await execute(() => exportSeasonStagesFn(fieldNames), {
        successMessageId: "seasonStages.export.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Season stages export was not started",
    );

  return {
    createSeasonStage,
    isCreatePending,
    updateSeasonStage,
    isUpdatePending,
    deleteSeasonStage,
    isDeletePending,
    importSeasonStages,
    isImportPending,
    exportSeasonStages,
    isExportPending,
  };
};
